import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { DialogSelectServiceComponent } from 'src/app/incidencias/operativas/dialog-select-service/dialog-select-service.component';
import { IncidentService } from 'src/app/services/api/incident.service';
import { CognitoService } from 'src/app/services/cognito.service';
import Swal from 'sweetalert2';
import { OpIncidentTypes } from '../incidents.types';

@Component({
  selector: 'app-dialog-add-op-incident',
  templateUrl: './dialog-add-op-incident.component.html',
  styleUrls: ['./dialog-add-op-incident.component.css']
})
export class DialogAddOpIncidentComponent implements OnInit {
  isSubmitting: boolean = false;
  textError: string | null = null;
  opIncidentForm!: FormGroup;
  availableServices: any[] = [];
  opIncidentTypes: OpIncidentTypes[] = [
    'Discrepancia en el itinerario pax / Biblia',
    'Falta de información y comunicación',
    'Errores en compra / reservas',
    'Tardanza u omisión de servicios',
    'Inconformidad de servicios',
    'Incidencia con aerolíneas',
    'Incidentes médicos',
    'Incidencias con trenes',
    'Robos o pérdidas',
    'Paros',
    'Otros'
  ];

  incidentStatus: string[] = ['CREATED', 'IN_PROGRESS', 'FINISHED'];

  selectedService: {
    serviceId: string;
    serviceName: string;
    serviceType: string;
    serviceProviderName?: string;
  } | null = null;

  fileNumber: any;
  authUsername: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly incidentService: IncidentService,
    private readonly cognitoService: CognitoService,
    public dialogRef: MatDialogRef<DialogAddOpIncidentComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: {
      itineraryId: string;
      fileNumber: string;

      // Servicio asociado a esta incidencia, en caso se cree una incidencia a partir de un incidente ya existente
      associatedService?: {
        serviceId: string;
        serviceName: string;
        serviceType: string;
      };
      associatedServiceIds?: string[];
      activityDayNumber?: number;
      activityDayName?: string;
      availableServices?: any[];
    },
    public dialog: MatDialog
  ) {
    console.log({
      DialogAddOpIncidentComponentData: this.data
    });
  }

  ngOnInit(): void {
    this.loadUser();
    this.opIncidentForm = this.formBuilder.group({
      serviceId: [undefined, Validators.nullValidator],
      opIncidentType: ['', Validators.required],
      incidentStatus: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(CREATED|IN_PROGRESS|FINISHED)$/)
        ]
      ],
      description: ['', Validators.required],

      solutionDate: [undefined, Validators.nullValidator],
      solutionTime: [undefined, [Validators.nullValidator, Validators.min(0)]],
      solutionTimeUnit: ['Hora(s)'],
      actions: [undefined, Validators.nullValidator]
    });
    this.availableServices = this.data.availableServices || [];
    this.fileNumber = this.data.fileNumber;

    if (this.data.associatedService?.serviceId) {
      this.selectedService = {
        serviceId: this.data.associatedService.serviceId,
        serviceName: this.data.associatedService.serviceName,
        serviceType: this.data.associatedService.serviceType
      };

      this.opIncidentForm.patchValue({
        serviceId: this.data.associatedService.serviceId
      });
    }
  }

  loadUser() {
    return this.cognitoService
      .getUsername()
      .then((username) => {
        console.log({ username });
        this.authUsername = username;
        return username;
      })
      .catch((err) => {
        console.error('Error fetching username:', err);
        this.textError =
          'No se pudo cargar usuario, por favor vuelva a iniciar sesión.';
        throw err;
      });
  }

  addOpIncident() {
    console.log({
      dialogData: this.data,
      incidentForm: this.opIncidentForm.value
    });

    if (!this.data.itineraryId) {
      Swal.fire({
        position: 'top-end',
        icon: 'error',
        title:
          'Error, el incidente debe estar asociado a un Itinerario, vuelva a intentar, de ser necesario, vuelve a iniciar sesión.',
        showConfirmButton: true
      });
      return;
    }

    if (this.opIncidentForm.invalid) {
      this.opIncidentForm.markAllAsTouched();
      return;
    }

    if (this.opIncidentForm.valid) {
      this.isSubmitting = true;

      const formValue = this.opIncidentForm.value;

      const payload = {
        itineraryId: this.data.itineraryId,
        serviceId: formValue.serviceId ? formValue.serviceId : undefined,

        opIncidentType: formValue.opIncidentType,
        incidentStatus: formValue.incidentStatus,
        description: formValue.description,
        reportedBy: this.authUsername!,

        actions: formValue.actions || undefined,
        solutionDate: formValue.solutionDate
          ? new Date(`${formValue.solutionDate}T00:00:00-05:00`).toISOString()
          : undefined,
        solutionTime: formValue.solutionTime
          ? `${formValue.solutionTime} ${formValue.solutionTimeUnit}`
          : undefined
      };

      this.incidentService
        .createOpIncident({
          itineraryId: payload.itineraryId,
          reportedBy: payload.reportedBy,
          opIncidentType: payload.opIncidentType,
          incidentStatus: payload.incidentStatus,
          description: payload.description,

          serviceId: payload.serviceId,

          actions: payload.actions,
          solutionDate: payload.solutionDate,
          solutionTime: payload.solutionTime
        })
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
            console.log('createOpIncident request completed');
          })
        )
        .subscribe({
          next: ({ createOpIncident }) => {
            console.log('Incidencia creada con éxito:', createOpIncident);
            if (createOpIncident) {
              this.dialogRef.close({
                event: 'op_incident_create',
                op_incident_create: {
                  ...payload,
                  id: createOpIncident,
                  createdAt: new Date().toISOString(),
                  serviceName: this.selectedService?.serviceName,
                  serviceType: this.selectedService?.serviceType,
                  serviceProviderName: this.selectedService?.serviceProviderName
                }
              });
              return;
            }
            Swal.fire({
              position: 'top-end',
              icon: 'error',
              title:
                'Error al crear la incidencia, por favor vuelva a intentar. Si el error persiste, vuelva a iniciar sesión.',
              showConfirmButton: true
            });
          },
          error: (error) => {
            console.error('Error al crear la incidencia:', error);
            Swal.fire({
              position: 'top-end',
              icon: 'error',
              title: 'Error al crear la incidencia',
              showConfirmButton: true
            });
          }
        });
    } else {
      // Si el formulario no es válido, marca todos los controles como tocados
      this.opIncidentForm.markAllAsTouched();
      console.warn(
        'El formulario no es válido. Por favor completa todos los campos requeridos.'
      );
    }
  }

  openDialogListServices() {
    const dialogConfig = new MatDialogConfig();
    console.log({ availableServices: this.availableServices });

    dialogConfig.data = {
      itineraryId: this.data.itineraryId,
      availableServices: this.availableServices,
      associatedServiceIds: this.data.associatedServiceIds,
      activityDayNumber: this.data.activityDayNumber,
      activityDayName: this.data.activityDayName
    };

    dialogConfig.width = '80%';
    dialogConfig.maxHeight = '750px';
    dialogConfig.maxWidth = '700px';

    const dialogRef = this.dialog.open(
      DialogSelectServiceComponent,
      dialogConfig
    );

    dialogRef.afterClosed().subscribe((result) => {
      console.log({ dialogResult: result });

      if (!result || result.event === 'nothing') return;
      if (!result.op_incident_append_service.serviceId || !this.authUsername) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title:
            'Parece que el servicio seleccionado no cargó correctamente, por favor vuelva a intentar. Si el error persiste, vuelva a iniciar sesión.'
        });
        return;
      }
      this.selectedService = {
        serviceId: result.op_incident_append_service.serviceId,
        serviceName: result.op_incident_append_service.serviceName,
        serviceType: result.op_incident_append_service.serviceType,
        serviceProviderName:
          result.op_incident_append_service.serviceProviderName
      };

      this.opIncidentForm.patchValue({
        serviceId: result.op_incident_append_service.serviceId
      });

      console.log({ opIncidentForm: this.opIncidentForm });
    });
  }
  removeService() {
    this.selectedService = null;
    this.opIncidentForm.patchValue({
      serviceId: null
    });
  }
  renameOpIncidentStatus(status: string) {
    switch (status) {
      case 'CREATED':
        return 'Creado';
      case 'IN_PROGRESS':
        return 'En Proceso';
      case 'FINISHED':
        return 'Solucionado';
      default:
        return status;
    }
  }
  closeDialog(): void {
    this.dialogRef.close({ event: 'nothing' });
  }
}
